/**
 * ============================================================================
 * BaseCart Web Component
 * ============================================================================
 * Base class for cart related web components. Provides reusable helpers for
 * talking to the Shopify Ajax Cart API (/cart.js, /cart/update.js, /cart/add.js).
 *
 * Any component that reads or writes to the cart should extend this class
 * instead of duplicating the fetch calls with the same error handling.
 *
 *   class CartWidget extends BaseCart { ... }
 */
class BaseCart extends HTMLElement {

    /**
     * ------------------------------------------------------------------------
     * CART API REQUEST HELPER
     * ------------------------------------------------------------------------
     * Low level fetch wrapper for the Shopify Ajax Cart API. Throws are caught
     * here and logged (no error bubbles up to the caller), so every public
     * helper below either resolves with the parsed JSON cart or null.
     *
     * @param {string} path - API endpoint, e.g. "/cart.js"
     * @param {object} options - fetch options (method, body, headers, ...)
     * @returns {Promise<object|null>}
     */
    async cartRequest(path, options = {}) {
        try {
            const response = await fetch(path, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                ...options
            });

            if (!response.ok) {
                throw new Error(`Request to ${path} failed with status ${response.status}`);
            }

            return await response.json();
        } catch (err) {
            console.error(`${this.constructor.name}: ${err.message}`);
            return null;
        }
    }

    /**
     * ------------------------------------------------------------------------
     * CART API READERS
     * ------------------------------------------------------------------------
     */

    /**
     * GET /cart.js
     * Fetch the current cart state (items, attributes, totals, ...).
     *
     * @returns {Promise<object|null>}
     */
    async fetchCart() {
        return this.cartRequest('/cart.js');
    }

    /**
     * ------------------------------------------------------------------------
     * CART API WRITERS
     * ------------------------------------------------------------------------
     */

    /**
     * POST /cart/update.js
     * Generic cart update. Accepts any valid update payload, e.g.:
     *   { attributes: {...} }  - update cart attributes
     *   { id: 'key', quantity } - change an item quantity
     *   { updates: {...} }      - change multiple quantities at once
     *
     * @param {object} payload - cart update payload
     * @returns {Promise<object|null>}
     */
    async updateCart(payload) {
        return this.cartRequest('/cart/update.js', {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    }

    /**
     * POST /cart/update.js { attributes }.
     * Shorthand for updating cart attributes.
     *
     * @param {object} attributes - e.g. { 'Pickup Date': '2024-01-01' }
     * @returns {Promise<object|null>}
     */
    async updateCartAttributes(attributes) {
        return this.updateCart({ attributes });
    }

    /**
     * POST /cart/update.js { id, quantity }.
     * Shorthand for changing a single item quantity (0 removes the item).
     *
     * @param {string} key - line item key
     * @param {number} quantity - new quantity
     * @returns {Promise<object|null>}
     */
    async updateCartItem(key, quantity) {
        return this.updateCart({ id: key, quantity });
    }

    /**
     * POST /cart/add.js { items }.
     * Shorthand for adding one or multiple items (variant id + quantity).
     *
     * @param {Array|object} items - one or an array of { id, quantity }
     * @returns {Promise<object|null>}
     */
    async addToCart(items) {
        const payload = Array.isArray(items) ? { items } : { items: [items] };
        return this.cartRequest('/cart/add.js', {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    }

    /**
     * POST /cart/clear.js.
     * Empties the entire cart.
     *
     * @returns {Promise<object|null>}
     */
    async clearCart() {
        return this.cartRequest('/cart/clear.js', { method: 'POST' });
    }
}

// Register the custom element
if (!customElements.get('cart-base')) {
    customElements.define('cart-base', BaseCart);
}